import * as _ from 'lodash';
import { useState } from 'react';
import { Dropdown, MenuItem } from 'react-bootstrap';
import { IDonorRegistration } from '@nkr/models/index';
import { DashRegistrationStep, DashDonationType, PreWorkupLabStep, PreWorkupLabJugStatus, RegistrationStep } from '@nkr/enums/index';
import { DonorIntakeAction } from './donor-intake-action';
import { canShowAdvReimburseAction } from '../../watr/components/watr-nav-area';
import { GridApi, RowNode } from 'ag-grid-community';
import OverlayMenuButton from '@nkr/components/overlay-menu-button';

const menuItemList = [
    { action: DonorIntakeAction.ViewDetails, title: 'View Details' },
    { action: DonorIntakeAction.ViewPwlDetails, title: 'View PWL Details' },
    { action: DonorIntakeAction.EditDiagnostics, title: 'Edit (Diagnostics)' },
    { action: DonorIntakeAction.GetPdf, title: 'Get PDF' },
    { action: DonorIntakeAction.UploadLabs, title: 'Upload Lab Results' },
    { action: DonorIntakeAction.ConvertToDirectToCenter, title: 'Convert - Direct to Center' },
    { action: DonorIntakeAction.ViewLabOrders, title: 'View Lab Orders' },
    { action: DonorIntakeAction.RestartPwlJug, title: 'Restart PWL (Jug)' },
    { action: DonorIntakeAction.RestartPwlCystatinC, title: 'Restart PWL (Cystatin C)' },
    { action: DonorIntakeAction.ExpireDonor, title: 'Expire Donor' },
    { action: DonorIntakeAction.OptOutDonor, title: 'Opt-Out Donor' },
    { action: DonorIntakeAction.LabResults, title: 'View Lab Results' },
    { action: DonorIntakeAction.ViewLabResultsDataGenerated, title: 'View Lab Results (Data Generated)' },
    { action: DonorIntakeAction.ShipJugAndGoToCenter, title: 'Ship Jug & Go to Center' },
    { action: DonorIntakeAction.StartPreWorkupLabs, title: 'Start Pre-Workup Labs' },
    { action: DonorIntakeAction.StartExpertSystem, title: 'Start Expert System' },
    { action: DonorIntakeAction.ManuallySendRegistrationLink, title: 'Manually Send Registration Link' },
    { action: DonorIntakeAction.AddPostOpFollowUpDonor, title: 'Add as Post-Op Follow-Up Donor' },
    { action: DonorIntakeAction.ApplyForDonorShieldReimbursement, title: 'Send Donor Shield Invitation' },
    { action: DonorIntakeAction.TransferDonor, title: 'Transfer Donor' },
    { action: DonorIntakeAction.CompleteForCandidate, title: 'Complete for Candidate' },
    { action: DonorIntakeAction.ConnectWithDonorMentor, title: 'Connect with Donor Mentor' },
    { action: DonorIntakeAction.MarkJugArrived, title: 'Mark Jug-Arrived / Send Email' },
    { action: DonorIntakeAction.RuleOut, title: 'Rule Out' },
    { action: DonorIntakeAction.ReverseRuleOut, title: 'Reverse Rule Out' },
    { action: DonorIntakeAction.SendEmail, title: 'Send Canned Response Email' },
    { action: DonorIntakeAction.SendPediatricGalleryEmail, title: 'Send Pediatric Gallery Email' },
    { action: DonorIntakeAction.AddIntendedMicrositeRecipient, title: 'Add Intended Microsite Recipient' },
    { action: -1, title: '' }
];

interface IProps {
    donor: IDonorRegistration,
    isSuperAdmin: boolean,
    showLinkToDetails: boolean,
    showBsMenu: boolean,
    api?: GridApi,
    node?: RowNode,
    onMenuDisplayed?: (id: number) => void,
    onMenuToggle: (show: boolean) => void,
    onItemSelected: (id: number, action: number) => void,
}

const ActionsDropdown = (props: IProps) => {
    const [placement, setPlacement] = useState('bottom');

    // Decide which way the menu should open based on the row's position within the
    // currently-scrolled viewport. Rows in the lower half open upward so the menu
    // isn't clipped by the bottom of the grid. Computed on open (not render) because
    // ag-grid virtualizes rows and doesn't re-render the cell renderer on scroll.
    const getPlacement = () => {
        let { api, node } = props;

        if (api == null || node == null || node.rowTop == null) {
            return 'bottom';
        }

        let range = api.getVerticalPixelRange();
        let rowOffset = node.rowTop - range.top;
        let viewportHeight = range.bottom - range.top;

        return rowOffset > viewportHeight / 2 ? 'top' : 'bottom';
    };

    const handleMenuToggle = (show: boolean) => {
        if (show) {
            setPlacement(getPlacement());
        }

        props.onMenuToggle(show);
    };

    const handleMenuSelection = (id: any, action: number) => {
        props.onItemSelected(id, action);
    };

    const handleSideBarMenuSelection = (e: any) => {
        let action = parseInt(e);

        props.onItemSelected(props.donor.id, action);
    };

    const getUrl = (action: DonorIntakeAction, id: number) => {
        let hrefPrefixOutside = `${__OUTSIDEURL__}/centers`;

        let { id: donorId, dashDomain, donorEditKey, centerId } = props.donor;

        let pwlLink = !props.isSuperAdmin ?
            '/center/' + centerId + '/donors/donor-intake/pwl/details/' + id
            :
            '/admin/dash/donor-intake/pwl/details/' + id;

        switch (action) {
            case DonorIntakeAction.ViewDetails: return { link: centerId > 0 ? `/center/${centerId}/donors/donor-intake/details/${donorId}` : `/admin/dash/donor-intake/details/${donorId}` };
            case DonorIntakeAction.ViewPwlDetails: return { link: pwlLink };
            case DonorIntakeAction.EditDiagnostics: return { link: centerId > 0 ? `/center/${centerId}/donors/donor-intake/diagnostics/${donorId}` : `/admin/dash/donor-intake/diagnostics/${donorId}` };
            case DonorIntakeAction.ShipJugAndGoToCenter: return { link: '/center/' + centerId + '/donors/donor-intake/confirm-address?id=' + donorId + '&action=ShipJug' };
            case DonorIntakeAction.StartPreWorkupLabs: return { link: '/center/' + centerId + '/donors/donor-intake/confirm-address?id=' + donorId + '&action=StartPwl' };
            case DonorIntakeAction.ManuallySendRegistrationLink: return { link: hrefPrefixOutside + '/donor-intake/send-registration-link?id=' + donorId };
            case DonorIntakeAction.AddPostOpFollowUpDonor: return { link: '/centers/follow-up-donors/add-from-intake?id=' + donorId };
            case DonorIntakeAction.TransferDonor: return { link: `${dashDomain}/register/transfer-donor/${donorId}/${donorEditKey}`, target: '_blank' };
            case DonorIntakeAction.SendEmail: return { link: `/center/${centerId}/donors/donor-intake/send-email/${donorId}`, target: '_blank' };
        }
    }
    const getMenuItems = () => {
        let menuItems: DonorIntakeAction[] = [];

        let { showLinkToDetails } = props;

        let {
            centerId, currentRegistrationStep, isNkrUser, labFileUploaded, labJsonFileUploaded, isExpertSystemParticipant,
            scripUploadFile, donationType, isRemoteDonor, preWorkupLabExists, canAccessFollowUp, preWorkupLabStep, jugStatus,
            preWorkupLabStartDate, donorConnectEmailSent, historyCompleteDateTime } = props.donor;

        let showRuleOut = currentRegistrationStep < RegistrationStep.AutoRuledOut;
        let showReverseRuleOut = (currentRegistrationStep == RegistrationStep.AutoRuledOut || currentRegistrationStep == RegistrationStep.TempRuleOut || currentRegistrationStep == RegistrationStep.ManuallyRuledOut) && (historyCompleteDateTime == '' || historyCompleteDateTime == null);
        let allowExpertSystem = isExpertSystemParticipant && donationType == DashDonationType.Kidney && props.donor.isInExpertSystem == false;

        if (currentRegistrationStep == DashRegistrationStep.Transferred) {
            menuItems.push(DonorIntakeAction.ViewDetails);
        } else {
            if (showLinkToDetails) {
                menuItems.push(DonorIntakeAction.ViewDetails);
            }

            if (preWorkupLabStartDate != '') {
                menuItems.push(DonorIntakeAction.ViewPwlDetails);
            }

            if (isNkrUser) {
                menuItems.push(DonorIntakeAction.EditDiagnostics);
            }

            menuItems.push(DonorIntakeAction.GetPdf);

            menuItems.push(DonorIntakeAction.Divider);

            if (props.isSuperAdmin) {
                menuItems.push(DonorIntakeAction.ConvertToDirectToCenter);
            }

            if (preWorkupLabStep != null
                && ([PreWorkupLabStep.PhlebotScripUploaded, PreWorkupLabStep.PhlebotScripDownloaded].indexOf(preWorkupLabStep) >= 0 || preWorkupLabStep >= PreWorkupLabStep.QuestScripUploaded)
                && props.isSuperAdmin) {
                menuItems.push(DonorIntakeAction.UploadLabs);
            }

            if (scripUploadFile) {
                menuItems.push(DonorIntakeAction.ViewLabOrders);
            }

            if (labFileUploaded) {
                menuItems.push(DonorIntakeAction.LabResults);
            }

            if (labJsonFileUploaded) {
                menuItems.push(DonorIntakeAction.ViewLabResultsDataGenerated);
            }

            if (props.isSuperAdmin) {
                menuItems.push(DonorIntakeAction.RestartPwlJug);
                menuItems.push(DonorIntakeAction.RestartPwlCystatinC);
            }

            menuItems.push(DonorIntakeAction.ExpireDonor);
            menuItems.push(DonorIntakeAction.OptOutDonor);

            if (donationType == DashDonationType.Kidney && centerId > 0) {
                menuItems.push(DonorIntakeAction.Divider);
                menuItems.push(DonorIntakeAction.ShipJugAndGoToCenter);
            }

            if (preWorkupLabStep != null
                && preWorkupLabStep >= PreWorkupLabStep.JugShipped
                && jugStatus != null && jugStatus < PreWorkupLabJugStatus.Received
                && props.isSuperAdmin) {
                menuItems.push(DonorIntakeAction.MarkJugArrived);
            }

            if (!isRemoteDonor && !preWorkupLabExists && currentRegistrationStep >= DashRegistrationStep.HistoryComplete && currentRegistrationStep < DashRegistrationStep.AutoRuledOut) {
                menuItems.push(DonorIntakeAction.StartPreWorkupLabs);
            }

            if (currentRegistrationStep == DashRegistrationStep.PreWorkupLabs) {
                menuItems.push(DonorIntakeAction.ManuallySendRegistrationLink);
            }

            if (canAccessFollowUp && donationType == DashDonationType.Kidney) {
                menuItems.push(-1);
                menuItems.push(DonorIntakeAction.AddPostOpFollowUpDonor);
            }

            if (allowExpertSystem) {
                menuItems.push(DonorIntakeAction.StartExpertSystem);
            }


            if (showRuleOut) {
                menuItems.push(-1);
                menuItems.push(DonorIntakeAction.RuleOut);
            }

            if (showReverseRuleOut) {
                menuItems.push(-1);
                menuItems.push(DonorIntakeAction.ReverseRuleOut);
            }

            menuItems.push(-1);

            if (props.donor.wageAndTravelRequested !== true
                && canShowAdvReimburseAction(
                    props.donor.centerId,
                    props.donor.donationType,
                    props.donor.countryOfResidenceId,
                    props.donor.isDcnParticipant
                )) {
                menuItems.push(DonorIntakeAction.ApplyForDonorShieldReimbursement);
            }

            menuItems.push(DonorIntakeAction.TransferDonor);

            if (currentRegistrationStep < DashRegistrationStep.HistoryComplete) {
                menuItems.push(-1);
                menuItems.push(DonorIntakeAction.CompleteForCandidate);
            }

            if (!donorConnectEmailSent) {
                menuItems.push(-1);
                menuItems.push(DonorIntakeAction.ConnectWithDonorMentor);
            }
        }

        if (centerId > 0) {
            menuItems.push(-1);
            menuItems.push(DonorIntakeAction.SendEmail);

            if (props.donor.type === 'FV' || props.donor.type === 'NDD') {
                menuItems.push(DonorIntakeAction.SendPediatricGalleryEmail);
                menuItems.push(DonorIntakeAction.AddIntendedMicrositeRecipient);
            }
        }

        return menuItems;
    }

    const menuItems = getMenuItems()
        .map(item => menuItemList.find(x => x.action == item))
        .filter(item => item != null);

    const menu = menuItems.map(item => {
        let url = item.action != -1 ? getUrl(item.action, props.donor.id) : null;
        let id = item.action >= 0 ? props.donor.id : 'div-' + props.donor.id + '-' + Math.random() * 10000;

        if (url == null) {
            if (item.action >= 0) {
                return { id, action: item.action, include: true, selectable: true, element: <>{item.title}</>, title: item.title };
            } else {
                return { id, action: item.action, include: true, selectable: false, divider: true, element: null };
            }
        } else {
            return { id, action: item.action, include: true, selectable: false, element: <a href={url.link}>{item.title}</a>, url: url.link, title: item.title };
        }
    });

    if (props.showBsMenu) {
        return (
            <Dropdown id={`action-menu-${props.donor.centerId}`} onSelect={handleSideBarMenuSelection} pullRight>
                <Dropdown.Toggle style={{ width: '100%' }}>
                    Donor Actions
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {menu.map((item, index) => {
                        if (item.action == DonorIntakeAction.Divider) {
                            return (
                                <MenuItem key={'divider-' + index} divider />
                            );
                        }
                        return (
                            <MenuItem
                                key={'action-' + index}
                                href={item.url}
                                eventKey={item.action}
                                data-testid={item.action === DonorIntakeAction.ApplyForDonorShieldReimbursement ? 'admin-invite-send-donor-shield' : undefined}
                            >
                                {item.title}
                            </MenuItem>
                        );
                    })}
                </Dropdown.Menu>
            </Dropdown>
        );
    }

    return (
        <OverlayMenuButton
            menu={menu}
            bsStyle='primary'
            bsSize='xs'
            title='Actions'
            placement={placement}
            listItemStyle={{ padding: '2px 10px', fontSize: 14 }}
            includeCaret={true}
            onToggle={handleMenuToggle}
            onSelect={handleMenuSelection}
        />
    );
}

export default ActionsDropdown;